import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import { RevenueDashboardView, C, applyTheme } from './Dashboard';
import { setFormatContext, fmtMoney, fmtPercent, fmtRate, fmtHours } from './format';
import { StatTiles, SegmentedControl } from './ui';

// /shared/dashboard/:token — email-gated, no TimeKeep account required. See
// supabase/migrations/20260909_dashboard_shares.sql for the full trust model
// (allowlist + emailed magic-link verification + a hashed, 30-day session
// token stored client-side only). Three edge functions drive this:
// request-dashboard-share-access (send the magic link), verify-dashboard-
// share-access (exchange it for a session), get-shared-dashboard (serve the
// actual data once verified — full raw rows for scope='full', reusing
// RevenueDashboardView itself so the math can never drift from what the
// admin sees; pre-aggregated numbers only for scope='summary', computed
// server-side so client/contractor-level detail never leaves the database).

const sessionKey = (token) => `tk_dashshare_session:${token}`;

export default function PublicSharedDashboard({ token }) {
  const [phase, setPhase] = useState('loading'); // loading | email | check-email | verify-error | dashboard
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [data, setData] = useState(null);
  const [summaryPeriod, setSummaryPeriod] = useState({ mode: 'month', selectedMonth: new Date().toISOString().slice(0,7), customStart: '', customEnd: '' });
  const verifyRanRef = useRef(false); // guards against StrictMode's dev-only double-invoke re-spending a single-use verify token

  // A shared link is viewed by people outside the org entirely, but the
  // numbers should still look like this org's own dashboard, not TimeKeep's
  // bare defaults -- applyTheme mutates the same C/css tokens RevenueDashboardView
  // and SharedSummaryView both read from.
  if (data?.org) setFormatContext({ currency: data.org.currency });
  useEffect(() => { if (data?.org) applyTheme(data.org); }, [data?.org]);

  const loadDashboard = useCallback(async (sessionToken, period) => {
    const { data: res, error } = await supabase.functions.invoke('get-shared-dashboard', {
      body: { share_token: token, session_token: sessionToken, period },
    });
    if (error || !res?.ok) {
      try { localStorage.removeItem(sessionKey(token)); } catch(_) {}
      setPhase('email');
      return false;
    }
    setData(res);
    setPhase('dashboard');
    return true;
  }, [token]);

  useEffect(() => {
    if (!token) { setErrorMsg('Invalid link.'); setPhase('verify-error'); return; }

    const params = new URLSearchParams(window.location.search);
    const vt = params.get('vt');

    (async () => {
      if (vt) {
        if (verifyRanRef.current) return;
        verifyRanRef.current = true;
        const { data: res, error } = await supabase.functions.invoke('verify-dashboard-share-access', {
          body: { share_token: token, verify_token: vt },
        });
        // Strip vt regardless of outcome -- a reload must not re-spend it.
        window.history.replaceState({}, '', window.location.pathname);
        if (error || !res?.ok) {
          setErrorMsg(res?.error || 'This link is invalid or has expired. Please request a new one.');
          setPhase('verify-error');
          return;
        }
        try { localStorage.setItem(sessionKey(token), res.session_token); } catch(_) {}
        await loadDashboard(res.session_token, summaryPeriod);
        return;
      }

      let stored = null;
      try { stored = localStorage.getItem(sessionKey(token)); } catch(_) {}
      if (stored) {
        await loadDashboard(stored, summaryPeriod);
      } else {
        setPhase('email');
      }
    })();
    // Deliberately only re-runs on token change -- summaryPeriod changes go
    // through changeSummaryPeriod below, not a full re-verify.
  }, [token, loadDashboard]); // eslint-disable-line

  const submitEmail = async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) { setErrorMsg('Enter a valid email address.'); return; }
    setSubmitting(true); setErrorMsg('');
    await supabase.functions.invoke('request-dashboard-share-access', { body: { share_token: token, email: trimmed } });
    setSubmitting(false);
    setPhase('check-email');
  };

  const changeSummaryPeriod = async (next) => {
    setSummaryPeriod(next);
    let stored = null;
    try { stored = localStorage.getItem(sessionKey(token)); } catch(_) {}
    if (stored) await loadDashboard(stored, next);
  };

  const lightPage = (children) => (
    <div style={{minHeight:'100vh',background:'#f1f5f9',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'Arial,Helvetica,sans-serif',padding:'20px'}}>
      <div style={{width:'min(440px,100%)',background:'#fff',borderRadius:'10px',boxShadow:'0 2px 12px rgba(0,0,0,0.08)',overflow:'hidden'}}>
        {children}
      </div>
    </div>
  );

  if (phase === 'loading') return lightPage(
    <div style={{padding:'48px 32px',textAlign:'center',color:'#64748b',fontSize:'14px'}}>Loading…</div>
  );

  if (phase === 'verify-error') return lightPage(
    <div style={{padding:'48px 32px',textAlign:'center'}}>
      <div style={{fontSize:'32px',marginBottom:'12px'}}>🔒</div>
      <div style={{fontSize:'16px',fontWeight:'700',color:'#1e293b',marginBottom:'6px'}}>Link Not Valid</div>
      <div style={{fontSize:'13px',color:'#64748b'}}>{errorMsg}</div>
      <button onClick={()=>{setErrorMsg('');setPhase('email');}}
        style={{marginTop:'20px',padding:'8px 20px',background:'#111D39',color:'#fff',border:'none',borderRadius:'6px',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>
        Try a different email
      </button>
    </div>
  );

  if (phase === 'email') return lightPage(
    <>
      <div style={{background:'#111D39',padding:'20px 32px'}}>
        <div style={{color:'#00A0C8',fontSize:'16px',fontWeight:'800'}}>TimeKeep</div>
        <div style={{color:'#94a3b8',fontSize:'12px',marginTop:'2px'}}>Shared Revenue Dashboard</div>
      </div>
      <div style={{padding:'32px'}}>
        <div style={{fontSize:'13px',color:'#475569',marginBottom:'20px',lineHeight:'1.6'}}>
          Someone shared a revenue dashboard with you. Enter your email below — if it's on the list, we'll send you a link to view it.
        </div>
        <div style={{marginBottom:'8px'}}>
          <label style={{display:'block',fontSize:'11px',fontWeight:'700',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'6px'}}>Email</label>
          <input
            type="email" autoFocus value={email}
            onChange={e=>{setEmail(e.target.value);setErrorMsg('');}}
            onKeyDown={e=>e.key==='Enter'&&submitEmail()}
            placeholder="you@example.com"
            style={{width:'100%',boxSizing:'border-box',padding:'10px 12px',fontSize:'14px',border:'1px solid #cbd5e1',borderRadius:'6px',outline:'none'}}
          />
        </div>
        {errorMsg && <div style={{fontSize:'12px',color:'#dc2626',marginBottom:'12px'}}>{errorMsg}</div>}
        <button
          onClick={submitEmail} disabled={submitting}
          style={{width:'100%',padding:'12px',marginTop:'8px',background:'#111D39',color:'#fff',border:'none',borderRadius:'8px',fontSize:'14px',fontWeight:'700',cursor:submitting?'default':'pointer',opacity:submitting?0.7:1}}
        >
          {submitting ? 'Sending…' : 'Continue'}
        </button>
      </div>
    </>
  );

  if (phase === 'check-email') return lightPage(
    <div style={{padding:'48px 32px',textAlign:'center'}}>
      <div style={{fontSize:'32px',marginBottom:'12px'}}>✉️</div>
      <div style={{fontSize:'16px',fontWeight:'700',color:'#1e293b',marginBottom:'8px'}}>Check your email</div>
      <div style={{fontSize:'13px',color:'#64748b',lineHeight:'1.6'}}>
        If <strong>{email.trim()}</strong> has access, a link is on its way — check your inbox (and spam folder). It's valid for 15 minutes.
      </div>
    </div>
  );

  if (phase === 'dashboard' && data) {
    const header = (
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:'16px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'8px'}}>
        <div>
          <div style={{color:C.accent,fontSize:'15px',fontWeight:'800'}}>{data.orgName}</div>
          <div style={{color:C.muted,fontSize:'11px',marginTop:'2px'}}>{data.label ? `${data.label} · ` : ''}Shared Revenue Dashboard{data.scope==='summary' ? ' (summary)' : ''}</div>
        </div>
        <div style={{color:C.muted,fontSize:'11px'}}>Shared via TimeKeep</div>
      </div>
    );

    return (
      <div style={{minHeight:'100vh',background:C.bg}}>
        {header}
        <div style={{padding:'20px 24px',maxWidth: data.scope==='full' ? '1400px' : '900px',margin:'0 auto'}}>
          {data.scope === 'full' ? (
            <RevenueDashboardView
              invoices={data.invoices} clients={data.clients} entries={data.entries}
              assignments={data.assignments} orgMembers={data.orgMembers} users={data.users}
              org={data.org} statements={data.statements}
              readOnly initialBillingTerms={data.billingTerms}
            />
          ) : (
            <SharedSummaryView data={data} period={summaryPeriod} onChangePeriod={changeSummaryPeriod} />
          )}
        </div>
      </div>
    );
  }

  return null;
}

function SharedSummaryView({ data, period, onChangePeriod }) {
  const setMode = (mode) => {
    if (mode === 'month') onChangePeriod({ ...period, mode, selectedMonth: period.selectedMonth || new Date().toISOString().slice(0,7) });
    else onChangePeriod({ ...period, mode });
  };
  const inputStyle = {background:C.surface,border:`1px solid ${C.border}`,borderRadius:'6px',color:C.text,fontSize:'12px',padding:'6px 10px'};

  // Same four tiles, names and basis lines as the Org Overview being shared
  // (src/Dashboard.js RevenueDashboardView). The figures are computed by
  // get-shared-dashboard with the same src/metrics.js the app uses.
  return (
    <div>
      <div style={{display:'flex',justifyContent:'flex-end',alignItems:'center',marginBottom:'20px',flexWrap:'wrap',gap:'10px'}}>
        {period.mode!=='month' && <span style={{fontSize:'12px',color:C.muted,marginRight:'auto'}}>{data.rangeLabel}</span>}
        <SegmentedControl options={[['month','Month'],['ytd','YTD'],['custom','Custom']]} value={period.mode} onChange={setMode} />
        {period.mode==='month' && (
          <input type="month" value={period.selectedMonth} onChange={e=>onChangePeriod({...period, selectedMonth:e.target.value})} style={inputStyle} />
        )}
        {period.mode==='custom' && (
          <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
            <input type="date" value={period.customStart} onChange={e=>onChangePeriod({...period, customStart:e.target.value})} style={inputStyle} />
            <span style={{color:C.muted,fontSize:'12px'}}>–</span>
            <input type="date" value={period.customEnd} onChange={e=>onChangePeriod({...period, customEnd:e.target.value})} style={inputStyle} />
          </div>
        )}
      </div>

      <StatTiles items={[
        { label:'Invoiced', value: fmtMoney(data.invoicedAmt), basis: `by service month · ${data.invoicedCount} invoice${data.invoicedCount===1?'':'s'}`,
          tip: "Sent and paid invoices in the selected period. Drafts don't count: they haven't been sent and can still change." },
        { label:'Collected', value: fmtMoney(data.collectedAmt), basis: 'paid, of invoiced in range',
          tip: 'The invoiced amount above that has been paid.' },
        { label:'Outstanding', value: fmtMoney(data.outstandingAmt),
          basis: data.overdueAmt > 0 ? <>as of today · <span style={{color:C.danger,fontWeight:600}}>{fmtMoney(data.overdueAmt)} overdue</span></> : 'as of today · none overdue',
          tip: 'Everything sent and unpaid, as of today. It ignores the period selector.' },
        { label:'Profit on invoiced', value: fmtMoney(data.profitInvoiced), tone: data.profitInvoiced < 0 ? 'danger' : undefined,
          basis: data.marginPct === null ? 'nothing invoiced in range' : `${fmtPercent(data.marginPct)} margin · after ${fmtMoney(data.paidToMembers)} member pay`,
          provisional: data.payProvisional || null,
          tip: "Invoiced minus member pay. Member pay is each member's approved invoice for the month, or an estimate until it's approved. Expense reimbursements aren't pay." },
      ]} />

      <div style={{display:'flex',flexWrap:'wrap',gap:'18px',marginTop:'18px',background:C.surface,border:`1px solid ${C.border}`,borderRadius:'10px',padding:'14px 18px'}}>
        {[
          ['Member pay', fmtMoney(data.paidToMembers) + (data.payProvisional ? ' (provisional)' : '')],
          ['Profit on collected', fmtMoney(data.profitCollected)],
          ['Margin on collected', fmtPercent(data.marginCollectedPct)],
          ['Invoiced per hour', data.effectiveRate===null ? '—' : fmtRate(Math.round(data.effectiveRate))],
          ['Billable hours', fmtHours(data.totalBillableHours)],
        ].map(([label, value]) => (
          <div key={label}><div style={{fontSize:'11px',color:C.muted,marginBottom:'2px'}}>{label}</div><div style={{fontSize:'16px',fontWeight:'700',color:C.textBright}}>{value}</div></div>
        ))}
      </div>
    </div>
  );
}
